// Group Insurance — drag-and-drop Form Builder (super admin).
// Sections & fields: add / edit / duplicate / delete / reorder (drag or arrows),
// per-field config (label, key, description, placeholder, required, options,
// min/max/step/default, validation, conditional logic, image, repeater
// relationships), desktop/tablet/mobile preview + Preview-as-Member.
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import FieldRenderer from "@/components/insurance/FieldRenderer";
import RepeaterField from "@/components/insurance/RepeaterField";
import {
  Plus, Pencil, Copy, Trash2, ArrowUp, ArrowDown, GripVertical, Loader2, Save,
  Monitor, Tablet, Smartphone, Eye, Upload, X, GitBranch,
} from "lucide-react";

const TYPE_META = [
  { id: "short_text", label: "Short Text" }, { id: "long_text", label: "Long Text" },
  { id: "number", label: "Number" }, { id: "email", label: "Email" },
  { id: "mobile", label: "Mobile Number" }, { id: "date", label: "Date" },
  { id: "time", label: "Time" }, { id: "datetime", label: "Date & Time" },
  { id: "radio", label: "Radio Button" }, { id: "checkbox", label: "Checkbox" },
  { id: "dropdown", label: "Dropdown" }, { id: "multi_select", label: "Multi-select" },
  { id: "yes_no", label: "Yes / No" }, { id: "rating", label: "Rating" },
  { id: "slider", label: "Slider" }, { id: "file_upload", label: "File Upload" },
  { id: "image_upload", label: "Image Upload" }, { id: "signature", label: "Signature" },
  { id: "heading", label: "Heading" }, { id: "description", label: "Description" },
  { id: "divider", label: "Divider" }, { id: "table", label: "Table" },
  { id: "insured_repeater", label: "Insured Person Repeater" },
];
const CONTENT_TYPES = ["heading", "description", "divider"];
const OPTION_TYPES = ["radio", "dropdown", "multi_select", "table"];

const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 10)}`;
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || uid("field");

function newField(type) {
  return {
    id: uid("f"), key: slug(TYPE_META.find(t => t.id === type)?.label), type,
    label: TYPE_META.find(t => t.id === type)?.label || "Field",
    description: "", placeholder: "", required: false, enabled: true,
    options: OPTION_TYPES.includes(type) ? ["Option 1", "Option 2"] : [],
    min: type === "rating" ? 1 : undefined, max: type === "rating" ? 5 : type === "slider" ? 100 : undefined,
    step: type === "slider" ? 1 : undefined, default: undefined,
    validation: {}, condition: null, image: "",
    repeater: type === "insured_repeater" ? { relationships: ["Spouse", "Father", "Mother", "Son", "Daughter"] } : undefined,
  };
}

export default function FormBuilder() {
  const [versions, setVersions] = useState([]);
  const [verId, setVerId] = useState("");
  const [form, setForm] = useState(null);       // full version doc
  const [schema, setSchema] = useState(null);   // editable copy
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [previewMode, setPreviewMode] = useState("desktop"); // desktop | tablet | mobile
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editField, setEditField] = useState(null); // { si, fi, field }
  const dragRef = useRef(null);

  const loadVersions = () => api.get("/insurance/admin/forms").then(r => {
    setVersions(r.data);
    if (!verId && r.data.length) setVerId((r.data.find(v => v.status === "published") || r.data[0]).id);
  }).catch(() => {});
  useEffect(() => { loadVersions(); }, []); // eslint-disable-line
  useEffect(() => {
    if (!verId) return;
    api.get(`/insurance/admin/forms/${verId}`).then(r => {
      setForm(r.data);
      setSchema(JSON.parse(JSON.stringify(r.data.schema)));
      setDirty(false);
    });
  }, [verId]);

  const published = versions.find(v => v.status === "published");
  const readonly = form?.status === "archived";

  const allAnswerFields = useMemo(() => (schema?.sections || []).flatMap(s => (s.fields || []).filter(f => !CONTENT_TYPES.includes(f.type) && f.type !== "insured_repeater")), [schema]);

  /* ---------- mutations ---------- */
  const mutate = (fn, audit) => {
    setSchema(s => {
      const next = JSON.parse(JSON.stringify(s));
      fn(next);
      if (audit) next.__audit = [...(next.__audit || []), audit];
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setBusy("save");
    try {
      const audit = schema.__audit || [];
      const clean = JSON.parse(JSON.stringify(schema));
      delete clean.__audit;
      const { data } = await api.put(`/insurance/admin/forms/${form.id}`, { schema: clean, audit });
      setForm(data);
      setSchema(JSON.parse(JSON.stringify(data.schema)));
      setDirty(false);
      toast.success("Form saved" + (form.status === "published" ? " (live — older submissions keep their original version)" : ""));
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(""); }
  };

  const publish = async () => {
    if (dirty) { toast.error("Save your changes before publishing"); return; }
    setBusy("publish");
    try {
      await api.post(`/insurance/admin/forms/${form.id}/publish`);
      toast.success(`Form v${form.version} published — members now see this version`);
      loadVersions();
      setVerId(form.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusy(""); }
  };

  const createVersion = async (fromId) => {
    try {
      const { data } = await api.post("/insurance/admin/forms", fromId ? { from_version_id: fromId } : {});
      toast.success(`Draft v${data.version} created`);
      loadVersions();
      setVerId(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  /* ---------- drag & drop ---------- */
  const onDragStart = (payload) => (e) => {
    dragRef.current = payload;
    e.dataTransfer.effectAllowed = "move";
  };
  const onDropToSection = (si, fi = null) => (e) => {
    e.preventDefault();
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    if (d.kind === "palette") {
      mutate(s => {
        const f = newField(d.fieldType);
        s.sections[si].fields.splice(fi ?? s.sections[si].fields.length, 0, f);
      }, { action: "question_added", meta: { type: d.fieldType } });
    } else if (d.kind === "field") {
      if (d.si === si && d.fi === fi) return;
      mutate(s => {
        const [moved] = s.sections[d.si].fields.splice(d.fi, 1);
        const target = fi ?? s.sections[si].fields.length;
        s.sections[si].fields.splice(d.si === si && fi !== null && fi > d.fi ? target - 1 : target, 0, moved);
      }, { action: "question_moved", meta: {} });
    }
  };

  const move = (si, fi, dir) => mutate(s => {
    const arr = s.sections[si].fields;
    const j = fi + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[fi], arr[j]] = [arr[j], arr[fi]];
  }, { action: "question_moved", meta: {} });

  const moveSection = (si, dir) => mutate(s => {
    const j = si + dir;
    if (j < 0 || j >= s.sections.length) return;
    [s.sections[si], s.sections[j]] = [s.sections[j], s.sections[si]];
  }, { action: "section_moved", meta: {} });

  if (!form || !schema) {
    return <Card className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></Card>;
  }

  const previewWidth = previewMode === "mobile" ? "max-w-sm" : previewMode === "tablet" ? "max-w-xl" : "max-w-3xl";

  return (
    <div className="space-y-4" data-testid="gi-form-builder">
      {/* version bar */}
      <Card className="p-4 flex flex-wrap items-center gap-2">
        <Select value={verId} onValueChange={setVerId}>
          <SelectTrigger className="w-64" data-testid="gi-fb-version"><SelectValue /></SelectTrigger>
          <SelectContent>
            {versions.map(v => <SelectItem key={v.id} value={v.id}>v{v.version} — {v.name} ({v.status})</SelectItem>)}
          </SelectContent>
        </Select>
        <Badge className={form.status === "published" ? "bg-emerald-100 text-teal-700" : form.status === "archived" ? "bg-slate-200 text-slate-600" : "bg-amber-100 text-amber-800"} data-testid="gi-fb-status">
          {form.status.toUpperCase()} · v{form.version}
        </Badge>
        {form.status === "published" && <span className="text-[11px] text-muted-foreground">live for members — edits apply to new views; old submissions keep v{form.version} snapshot</span>}
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => createVersion(published?.id)} data-testid="gi-fb-new-version"><Plus className="w-4 h-4 mr-1" /> New Version</Button>
        <Button size="sm" variant="outline" onClick={() => createVersion(form.id)} data-testid="gi-fb-duplicate"><Copy className="w-4 h-4 mr-1" /> Duplicate</Button>
        {!readonly && (
          <Button size="sm" onClick={save} disabled={!dirty || !!busy} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="gi-fb-save">
            {busy === "save" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save{dirty ? " *" : ""}
          </Button>
        )}
        {form.status !== "published" && !readonly && (
          <Button size="sm" variant="outline" onClick={publish} disabled={!!busy} data-testid="gi-fb-publish">Publish</Button>
        )}
        {form.status === "draft" && (
          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => api.delete(`/insurance/admin/forms/${form.id}`).then(() => { toast.success("Draft deleted"); setVerId(""); loadVersions(); }).catch(e => toast.error(e?.response?.data?.detail || "Failed"))} data-testid="gi-fb-delete"><Trash2 className="w-4 h-4" /></Button>
        )}
        <div className="w-full flex items-center gap-1.5 pt-1">
          <span className="text-[11px] text-muted-foreground mr-1">Preview:</span>
          {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([m, Icon]) => (
            <Button key={m} size="icon" variant={previewMode === m ? "default" : "outline"} className={`h-8 w-8 ${previewMode === m ? "bg-teal-600 hover:bg-teal-700" : ""}`} onClick={() => setPreviewMode(m)} data-testid={`gi-fb-preview-${m}`}>
              <Icon className="w-4 h-4" />
            </Button>
          ))}
          <Button size="sm" variant="outline" className="h-8 ml-2" onClick={() => setPreviewOpen(true)} data-testid="gi-fb-preview-member"><Eye className="w-4 h-4 mr-1" /> Preview as Member</Button>
        </div>
      </Card>

      <div className="grid lg:grid-cols-[220px_1fr] gap-4">
        {/* field palette */}
        <Card className="p-3 h-fit lg:sticky lg:top-20" data-testid="gi-fb-palette">
          <div className="text-xs font-bold mb-2 uppercase tracking-wide text-muted-foreground">Field Types</div>
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-1">
            {TYPE_META.map(t => (
              <button key={t.id} type="button" draggable={!readonly}
                onDragStart={onDragStart({ kind: "palette", fieldType: t.id })}
                onClick={() => {
                  const si = schema.sections.length - 1;
                  if (si < 0) return;
                  mutate(s => { s.sections[si].fields.push(newField(t.id)); }, { action: "question_added", meta: { type: t.id } });
                }}
                disabled={readonly}
                className="text-left text-xs rounded-md border border-border px-2 py-1.5 hover:bg-muted/60 cursor-grab active:cursor-grabbing disabled:opacity-50"
                data-testid={`gi-fb-palette-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">Drag onto a section, or click to append to the last section.</p>
          <Button size="sm" variant="outline" className="w-full mt-3" disabled={readonly}
            onClick={() => mutate(s => { s.sections.push({ id: uid("s"), key: uid("sec"), title_ml: "New Section", title_en: "", description: "", enabled: true, fields: [] }); }, { action: "section_added", meta: {} })}
            data-testid="gi-fb-add-section">
            <Plus className="w-4 h-4 mr-1" /> Add Section
          </Button>
        </Card>

        {/* sections */}
        <div className="space-y-3">
          {schema.sections.map((sec, si) => (
            <Card key={sec.id} className={`p-4 ${sec.enabled === false ? "opacity-60" : ""}`}
              onDragOver={e => e.preventDefault()}
              onDrop={onDropToSection(si)}
              data-testid={`gi-fb-section-${si}`}>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" draggable onDragStart={onDragStart({ kind: "section", si })} onDragOver={e => e.preventDefault()} onDrop={(e) => {
                  e.preventDefault();
                  const d = dragRef.current;
                  if (d?.kind === "section" && d.si !== si) {
                    mutate(s => { const [m] = s.sections.splice(d.si, 1); s.sections.splice(si, 0, m); }, { action: "section_moved", meta: {} });
                  }
                  dragRef.current = null;
                }} />
                <Input value={sec.title_ml} disabled={readonly} onChange={e => mutate(s => { s.sections[si].title_ml = e.target.value; })}
                  className="h-8 w-56 text-sm font-semibold" style={{ fontFamily: "'Anek Malayalam', sans-serif" }} data-testid={`gi-fb-sec-title-ml-${si}`} />
                <Input value={sec.title_en} disabled={readonly} onChange={e => mutate(s => { s.sections[si].title_en = e.target.value; })}
                  className="h-8 w-44 text-xs" placeholder="English title" data-testid={`gi-fb-sec-title-en-${si}`} />
                <Switch checked={sec.enabled !== false} disabled={readonly} onCheckedChange={v => mutate(s => { s.sections[si].enabled = v; })} data-testid={`gi-fb-sec-enabled-${si}`} />
                <div className="flex-1" />
                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={readonly || si === 0} onClick={() => moveSection(si, -1)} data-testid={`gi-fb-sec-up-${si}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={readonly || si === schema.sections.length - 1} onClick={() => moveSection(si, 1)} data-testid={`gi-fb-sec-down-${si}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" disabled={readonly}
                  onClick={() => mutate(s => { s.sections.splice(si, 1); }, { action: "section_deleted", meta: { title: sec.title_ml } })}
                  data-testid={`gi-fb-sec-del-${si}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>

              <div className="space-y-1.5">
                {(sec.fields || []).map((f, fi) => (
                  <div key={f.id} draggable={!readonly}
                    onDragStart={onDragStart({ kind: "field", si, fi })}
                    onDragOver={e => e.preventDefault()}
                    onDrop={onDropToSection(si, fi)}
                    className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-2 cursor-grab active:cursor-grabbing"
                    data-testid={`gi-fb-field-${si}-${fi}`}>
                    <GripVertical className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs font-medium truncate flex-1">{f.label}</span>
                    <Badge variant="outline" className="text-[10px] shrink-0">{TYPE_META.find(t => t.id === f.type)?.label}</Badge>
                    {f.required && <Badge className="text-[10px] bg-red-100 text-red-700 shrink-0">Required</Badge>}
                    {f.condition && <GitBranch className="w-3.5 h-3.5 text-indigo-500 shrink-0" aria-label="has condition" />}
                    <Button size="icon" variant="ghost" className="h-6 w-6" disabled={readonly} onClick={() => setEditField({ si, fi, field: JSON.parse(JSON.stringify(f)) })} data-testid={`gi-fb-field-edit-${si}-${fi}`}><Pencil className="w-3 h-3" /></Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6" disabled={readonly} onClick={() => mutate(s => { const c = JSON.parse(JSON.stringify(s.sections[si].fields[fi])); c.id = uid("f"); c.key = slug(c.key + "_copy"); c.label += " (Copy)"; s.sections[si].fields.splice(fi + 1, 0, c); }, { action: "question_duplicated", meta: {} })} data-testid={`gi-fb-field-dup-${si}-${fi}`}><Copy className="w-3 h-3" /></Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6" disabled={readonly || fi === 0} onClick={() => move(si, fi, -1)} data-testid={`gi-fb-field-up-${si}-${fi}`}><ArrowUp className="w-3 h-3" /></Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6" disabled={readonly || fi === sec.fields.length - 1} onClick={() => move(si, fi, 1)} data-testid={`gi-fb-field-down-${si}-${fi}`}><ArrowDown className="w-3 h-3" /></Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600" disabled={readonly} onClick={() => mutate(s => { s.sections[si].fields.splice(fi, 1); }, { action: "question_deleted", meta: { label: f.label } })} data-testid={`gi-fb-field-del-${si}-${fi}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
                {!sec.fields?.length && <p className="text-xs text-muted-foreground text-center py-3 border border-dashed rounded-md">Drop field types here</p>}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* live preview strip */}
      <Card className="p-4" data-testid="gi-fb-live-preview">
        <div className="text-xs font-bold mb-3 uppercase tracking-wide text-muted-foreground">Live Preview ({previewMode})</div>
        <div className={`mx-auto ${previewWidth} transition-all border border-border rounded-lg p-4 bg-background space-y-4`}>
          {schema.sections.filter(s => s.enabled !== false).map(sec => (
            <div key={sec.id}>
              <h4 className="font-bold text-sm text-teal-900">{sec.title_en || sec.title_ml}</h4>
              <div className="space-y-3 mt-2">
                {(sec.fields || []).filter(f => f.enabled !== false).map(f => (
                  f.type === "insured_repeater"
                    ? <RepeaterField key={f.id} field={f} value={[]} onChange={() => {}} />
                    : <FieldRenderer key={f.id} field={f} value={f.type === "checkbox" ? false : ""} onChange={() => {}} answers={{}} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* preview as member */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="gi-fb-member-preview">
          <DialogHeader><DialogTitle>Preview as Member — form v{form.version}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            {[["KELTA ID", "KELTA-2026-00042"], ["Name", "Sample Member"], ["Designation", "Assistant Engineer"],
              ["Local Body", "Sample Panchayat"], ["District", "Ernakulam"], ["Mobile", "9876543210"]].map(([k, v]) => (
              <div key={k} className="rounded-md bg-muted/50 border border-border px-2.5 py-1.5">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
                <div className="text-xs font-semibold">{v}</div>
              </div>
            ))}
          </div>
          <MemberPreview schema={schema} />
        </DialogContent>
      </Dialog>

      {/* field config dialog */}
      <FieldConfigDialog
        value={editField}
        allFields={allAnswerFields}
        onClose={() => setEditField(null)}
        onSave={(nf) => {
          mutate(s => { s.sections[editField.si].fields[editField.fi] = nf; }, { action: "question_updated", meta: { label: nf.label } });
          setEditField(null);
        }}
      />
    </div>
  );
}

/* interactive member-style preview with working conditional logic */
function MemberPreview({ schema }) {
  const [answers, setAnswers] = useState({});
  const [persons, setPersons] = useState([]);
  return (
    <div className="space-y-4">
      {schema.sections.filter(s => s.enabled !== false).map(sec => (
        <Card key={sec.id} className="p-4">
          <h4 className="font-bold text-sm text-teal-900">{sec.title_en || sec.title_ml}</h4>
          <div className="space-y-3 mt-2">
            {(sec.fields || []).filter(f => f.enabled !== false).map(f => (
              f.type === "insured_repeater"
                ? <RepeaterField key={f.id} field={f} value={persons} onChange={setPersons} />
                : <FieldRenderer key={f.id} field={f} value={answers[f.key]} onChange={v => setAnswers(a => ({ ...a, [f.key]: v }))} answers={answers} />
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

/* per-field configuration dialog */
function FieldConfigDialog({ value, allFields, onClose, onSave }) {
  const [f, setF] = useState(null);
  useEffect(() => { setF(value ? value.field : null); }, [value]);
  if (!value || !f) return null;
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const isContent = CONTENT_TYPES.includes(f.type);
  const needsOptions = OPTION_TYPES.includes(f.type);
  const condKeyOptions = allFields.filter(x => x.id !== f.id);

  return (
    <Dialog open={!!value} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="gi-fb-field-dialog">
        <DialogHeader><DialogTitle>Configure Field — {TYPE_META.find(t => t.id === f.type)?.label}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Label</Label>
            <Input value={f.label} onChange={e => set("label", e.target.value)} data-testid="gi-fb-cfg-label" />
          </div>
          {!isContent && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Key (data field name, a-z 0-9 _)</Label>
              <Input value={f.key} onChange={e => set("key", slug(e.target.value))} className="font-mono" data-testid="gi-fb-cfg-key" />
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-semibold">Description (helper text)</Label>
            <Input value={f.description || ""} onChange={e => set("description", e.target.value)} data-testid="gi-fb-cfg-description" />
          </div>
          {!isContent && f.type !== "checkbox" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Placeholder</Label>
              <Input value={f.placeholder || ""} onChange={e => set("placeholder", e.target.value)} data-testid="gi-fb-cfg-placeholder" />
            </div>
          )}
          {!isContent && (
            <label className="flex items-center gap-2 pt-6 text-sm">
              <Switch checked={!!f.required} onCheckedChange={v => set("required", v)} data-testid="gi-fb-cfg-required" /> Required
            </label>
          )}
          {needsOptions && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-semibold">{f.type === "table" ? "Column headers" : "Options"} (one per line)</Label>
              <Textarea rows={3} value={(f.options || []).join("\n")} onChange={e => set("options", e.target.value.split("\n"))} data-testid="gi-fb-cfg-options" />
            </div>
          )}
          {(f.type === "number" || f.type === "slider" || f.type === "rating") && (
            <>
              <div className="space-y-1.5"><Label className="text-xs font-semibold">Min</Label><Input type="number" value={f.min ?? ""} onChange={e => set("min", e.target.value === "" ? undefined : +e.target.value)} data-testid="gi-fb-cfg-min" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-semibold">Max</Label><Input type="number" value={f.max ?? ""} onChange={e => set("max", e.target.value === "" ? undefined : +e.target.value)} data-testid="gi-fb-cfg-max" /></div>
              {f.type === "slider" && (
                <div className="space-y-1.5"><Label className="text-xs font-semibold">Step</Label><Input type="number" value={f.step ?? 1} onChange={e => set("step", +e.target.value || 1)} data-testid="gi-fb-cfg-step" /></div>
              )}
            </>
          )}
          {!isContent && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Default value</Label>
              <Input value={f.default ?? ""} onChange={e => set("default", e.target.value === "" ? undefined : e.target.value)} data-testid="gi-fb-cfg-default" />
            </div>
          )}
          {f.type === "insured_repeater" && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-semibold">Permitted relationships (one per line)</Label>
              <Textarea rows={4} value={(f.repeater?.relationships || []).join("\n")}
                onChange={e => set("repeater", { relationships: e.target.value.split("\n").filter(Boolean) })}
                data-testid="gi-fb-cfg-relationships" />
              <p className="text-[11px] text-muted-foreground">Name, Relationship, Date of Birth, auto-Age and Gender are collected automatically for each person.</p>
            </div>
          )}

          {/* conditional logic */}
          {!isContent && (
            <div className="sm:col-span-2 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2">
              <div className="flex items-center gap-2 text-xs font-bold text-indigo-800"><GitBranch className="w-3.5 h-3.5" /> Conditional visibility</div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={f.condition ? "yes" : "no"} onValueChange={v => set("condition", v === "yes" ? { key: condKeyOptions[0]?.key || "", op: "equals", value: "" } : null)}>
                  <SelectTrigger className="w-44 h-8 text-xs bg-white" data-testid="gi-fb-cfg-cond-toggle"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">Always visible</SelectItem>
                    <SelectItem value="yes">Visible only when…</SelectItem>
                  </SelectContent>
                </Select>
                {f.condition && (
                  <>
                    <Select value={f.condition.key} onValueChange={v => set("condition", { ...f.condition, key: v, value: "" })}>
                      <SelectTrigger className="w-56 h-8 text-xs bg-white" data-testid="gi-fb-cfg-cond-key"><SelectValue placeholder="question" /></SelectTrigger>
                      <SelectContent>
                        {condKeyOptions.map(x => <SelectItem key={x.id} value={x.key}>{x.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={f.condition.op} onValueChange={v => set("condition", { ...f.condition, op: v })}>
                      <SelectTrigger className="w-32 h-8 text-xs bg-white" data-testid="gi-fb-cfg-cond-op"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="equals">equals</SelectItem>
                        <SelectItem value="not_equals">not equals</SelectItem>
                        <SelectItem value="in">is one of</SelectItem>
                      </SelectContent>
                    </Select>
                    <CondValue field={f} source={condKeyOptions.find(x => x.key === f.condition.key)} onChange={v => set("condition", { ...f.condition, value: v })} />
                  </>
                )}
              </div>
            </div>
          )}

          {/* image for any field (instructional graphics) */}
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-semibold">Instructional image (optional)</Label>
            <div className="flex items-center gap-2">
              {f.image && <img src={f.image} alt="" className="h-12 rounded border" />}
              <label className="inline-flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs cursor-pointer hover:bg-muted/50">
                <Upload className="w-3.5 h-3.5" /> Upload image
                <input type="file" accept="image/*" className="hidden" data-testid="gi-fb-cfg-image"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 2 * 1024 * 1024) { toast.error("Image under 2 MB please"); return; }
                    const r = new FileReader();
                    r.onload = () => set("image", r.result);
                    r.readAsDataURL(file);
                  }} />
              </label>
              {f.image && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => set("image", "")}><X className="w-3.5 h-3.5" /></Button>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(f)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="gi-fb-cfg-save">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CondValue({ field, source, onChange }) {
  const cond = field.condition;
  if (!source) return null;
  const opts = source.type === "yes_no" ? ["Yes", "No"] : (source.options || []);
  if (cond.op === "in") {
    return (
      <Input className="h-8 w-64 text-xs bg-white" placeholder="values, comma separated"
        value={Array.isArray(cond.value) ? cond.value.join(", ") : cond.value || ""}
        onChange={e => onChange(e.target.value.split(",").map(x => x.trim()).filter(Boolean))}
        data-testid="gi-fb-cfg-cond-value" />
    );
  }
  if (opts.length) {
    return (
      <Select value={cond.value || ""} onValueChange={onChange}>
        <SelectTrigger className="w-56 h-8 text-xs bg-white" data-testid="gi-fb-cfg-cond-value"><SelectValue placeholder="value" /></SelectTrigger>
        <SelectContent>{opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  return <Input className="h-8 w-56 text-xs bg-white" value={cond.value || ""} onChange={e => onChange(e.target.value)} placeholder="value" data-testid="gi-fb-cfg-cond-value" />;
}
